import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getDb } from "@/lib/offlineDb";
import { getStyleConfig, WORKOUT_STYLES, type WorkoutStyleConfig } from "@/lib/workoutStyles";

interface ActiveStyleContextValue {
  /** Resolved active style (checks expiry, falls back to preferred_style) */
  activeStyle: string;
  activeStyleConfig: WorkoutStyleConfig;
  /** Source of the current active style */
  activeStyleSource: string;
  /** User's primary/default style */
  preferredStyle: string;
  /** All styles the user has enabled */
  enabledStyles: string[];
  /** Change the active style (optionally with expiry) */
  setActiveStyle: (styleId: string, source: "manual" | "gym_wallet" | "coach_plan", expiresAt?: string | null) => Promise<void>;
  /** Revert to preferred_style */
  revertToDefault: () => Promise<void>;
  loading: boolean;
}

const ActiveStyleContext = createContext<ActiveStyleContextValue | null>(null);

export function ActiveStyleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [preferredStyle, setPreferredStyle] = useState("strength_general");
  const [secondaryStyles, setSecondaryStyles] = useState<string[]>([]);
  const [activeStyleRaw, setActiveStyleRaw] = useState<string | null>(null);
  const [activeStyleSource, setActiveStyleSourceState] = useState("manual");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Resolve active style: check expiry
  const isExpired = expiresAt ? new Date(expiresAt) <= new Date() : false;
  const activeStyle = (!activeStyleRaw || isExpired) ? preferredStyle : activeStyleRaw;
  const activeStyleConfig = getStyleConfig(activeStyle);

  const enabledStyles = Array.from(new Set([preferredStyle, ...secondaryStyles]));

  // Load from profile
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const load = async () => {
      try {
        // Try IDB first for offline
        const db = await getDb();
        const cached = await db.get("profiles", user.id);
        if (cached) {
          setPreferredStyle(cached.preferred_style || "strength_general");
          setSecondaryStyles(cached.secondary_styles || []);
          setActiveStyleRaw(cached.active_style || null);
          setActiveStyleSourceState(cached.active_style_source || "manual");
          setExpiresAt(cached.active_style_expires_at || null);
        }
        // Then fetch fresh from server
        const { data } = await supabase
          .from("profiles")
          .select("preferred_style, secondary_styles, active_style, active_style_source, active_style_expires_at")
          .eq("user_id", user.id)
          .maybeSingle();
        if (data) {
          setPreferredStyle(data.preferred_style || "strength_general");
          setSecondaryStyles((data as any).secondary_styles || []);
          setActiveStyleRaw((data as any).active_style || null);
          setActiveStyleSourceState((data as any).active_style_source || "manual");
          setExpiresAt((data as any).active_style_expires_at || null);
          // Update IDB cache
          try {
            const existing = await db.get("profiles", user.id);
            if (existing) {
              await db.put("profiles", {
                ...existing,
                active_style: (data as any).active_style,
                active_style_source: (data as any).active_style_source,
                active_style_expires_at: (data as any).active_style_expires_at,
              });
            }
          } catch { /* ignore */ }
        }
      } catch { /* offline fallback already loaded */ }
      setLoading(false);
    };
    load();
  }, [user]);

  // Auto-revert expired style on interval
  useEffect(() => {
    if (!expiresAt) return;
    const check = () => {
      if (new Date(expiresAt) <= new Date()) {
        setActiveStyleRaw(null);
        setExpiresAt(null);
        setActiveStyleSourceState("manual");
      }
    };
    check();
    const interval = setInterval(check, 60_000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const setActiveStyle = useCallback(async (
    styleId: string,
    source: "manual" | "gym_wallet" | "coach_plan",
    newExpiresAt?: string | null,
  ) => {
    setActiveStyleRaw(styleId);
    setActiveStyleSourceState(source);
    setExpiresAt(newExpiresAt ?? null);

    if (user) {
      // Update IDB immediately
      try {
        const db = await getDb();
        const existing = await db.get("profiles", user.id);
        if (existing) {
          await db.put("profiles", {
            ...existing,
            active_style: styleId,
            active_style_source: source,
            active_style_expires_at: newExpiresAt ?? null,
          });
        }
      } catch { /* ignore */ }

      // Update server
      try {
        await supabase
          .from("profiles")
          .update({
            active_style: styleId,
            active_style_source: source,
            active_style_expires_at: newExpiresAt ?? null,
          } as any)
          .eq("user_id", user.id);
      } catch { /* offline — IDB already updated */ }
    }
  }, [user]);

  const revertToDefault = useCallback(async () => {
    setActiveStyleRaw(null);
    setExpiresAt(null);
    setActiveStyleSourceState("manual");

    if (user) {
      try {
        const db = await getDb();
        const existing = await db.get("profiles", user.id);
        if (existing) {
          await db.put("profiles", {
            ...existing,
            active_style: null,
            active_style_source: "manual",
            active_style_expires_at: null,
          });
        }
      } catch { /* ignore */ }

      try {
        await supabase
          .from("profiles")
          .update({
            active_style: null,
            active_style_source: "manual",
            active_style_expires_at: null,
          } as any)
          .eq("user_id", user.id);
      } catch { /* ignore */ }
    }
  }, [user]);

  return (
    <ActiveStyleContext.Provider
      value={{
        activeStyle,
        activeStyleConfig,
        activeStyleSource,
        preferredStyle,
        enabledStyles,
        setActiveStyle,
        revertToDefault,
        loading,
      }}
    >
      {children}
    </ActiveStyleContext.Provider>
  );
}

const fallback: ActiveStyleContextValue = {
  activeStyle: "strength_general",
  activeStyleConfig: getStyleConfig("strength_general"),
  activeStyleSource: "manual",
  preferredStyle: "strength_general",
  enabledStyles: ["strength_general"],
  setActiveStyle: async () => {},
  revertToDefault: async () => {},
  loading: true,
};

export function useActiveStyle() {
  const ctx = useContext(ActiveStyleContext);
  return ctx ?? fallback;
}
